import { useEffect, useState } from "react";
import {
  Code, File, FileArchive, FileText, MessagesSquare, Play, Presentation, Sheet as SheetIcon, TriangleAlert, AudioLines, WandSparkles,
} from "lucide-react";
import { HermesError, assetRef, mediaExtension, mediaFileName, type AssetItem } from "@timibot/core";
import { useApp } from "../../context";
import { mediaCache } from "../../state/media";
import { useObserve } from "../../state/observable";
import { Spinner } from "../../ui/components";
import { MediaGallery } from "../chat/Media";
import "./workspace.css";

type Filter = "all" | "image" | "video" | "file";

/** 全部作品：对话里产出的图片、视频、文件，按时间分组 */
export function AssetsView({ onOpen }: { onOpen: (item: AssetItem) => void }) {
  const app = useApp();
  useObserve(app.assets);
  const [filter, setFilter] = useState<Filter>("all");
  useEffect(() => { void app.refreshAssets(); }, [app.workspaceScope]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = app.assets.assets.filter((x) => filter === "all" || (filter === "file" ? x.kind === "file" || x.kind === "audio" : x.kind === filter));
  const filters: [Filter, string][] = [["all", "全部"], ["image", "图片"], ["video", "视频"], ["file", "文件"]];
  return (
    <div className="assets-page">
      <div className="chip-row">
        {filters.map(([f, title]) => <button key={f} className={`chip${filter === f ? " on" : ""}`} onClick={() => setFilter(f)}>{title}</button>)}
        {app.assets.isScanning && <Spinner size={14} />}
      </div>
      {app.assets.error && <div className="t-footnote t-danger">{app.assets.error}</div>}
      {app.assets.loaded && items.length === 0 && (
        <div className="empty-state"><div className="t-headline">还没有作品</div><div className="t-subheadline t-2">对话里生成的图片、视频、文件都会收在这里。</div></div>
      )}
      {buckets(items).map(([title, bucket]) => (
        <div key={title} className="col" style={{ gap: 8 }}>
          <span className="t-footnote semibold t-2">{title}</span>
          <div className="asset-grid">
            {bucket.map((item) => <button key={item.source} className="asset-cell" onClick={() => onOpen(item)}><AssetThumb item={item} /></button>)}
          </div>
        </div>
      ))}
      {!app.assets.loaded && !app.assets.assets.length && <div className="center-pad"><Spinner /></div>}
    </div>
  );
}

function buckets(items: AssetItem[]): [string, AssetItem[]][] {
  const order: string[] = [];
  const groups = new Map<string, AssetItem[]>();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  for (const item of items) {
    const t = item.timestamp * 1000;
    const d = new Date(t);
    const key = t >= today ? "今天" : t >= today - 86400000 ? "昨天" : now.getTime() - t < 7 * 86400000 ? "最近 7 天" : `${d.getFullYear()}年${d.getMonth() + 1}月`;
    if (!groups.has(key)) { order.push(key); groups.set(key, []); }
    groups.get(key)!.push(item);
  }
  return order.map((k) => [k, groups.get(k)!]);
}

/** 方形缩略图：图片取缩略图，视频取首帧，其它文件显示图标和文件名 */
export function AssetThumb({ item, showName = true }: { item: AssetItem; showName?: boolean }) {
  const app = useApp();
  const ref = assetRef(item);
  const [url, setURL] = useState<string | undefined>(() => (ref.kind === "image" || ref.kind === "video" ? mediaCache.cachedURL(ref) : undefined));
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (url || (ref.kind !== "image" && ref.kind !== "video")) return;
    let cancelled = false;
    mediaCache.url(ref, app.api).then((u) => { if (!cancelled) setURL(u); }, (e) => {
      if (cancelled) return;
      setFailed(true);
      const permanent = e instanceof HermesError && e.kind === "http" && [403, 404, 410].includes(e.status ?? 0);
      app.assets.markUnavailable(ref.source, permanent);
    });
    return () => { cancelled = true; };
  }, [ref.source, app.api]); // eslint-disable-line react-hooks/exhaustive-deps

  if (url && ref.kind === "image") return <img className="asset-thumb" src={url} alt="" loading="lazy" />;
  if (url && ref.kind === "video") {
    return (
      <span className="asset-thumb video">
        <video src={url} preload="metadata" muted playsInline />
        <Play size={12} fill="#fff" className="asset-play" />
      </span>
    );
  }
  const Icon = failed ? TriangleAlert : fileSymbol(ref.kind === "audio" ? "audio" : mediaExtension(ref));
  return (
    <span className="asset-thumb file">
      {ref.kind === "image" || ref.kind === "video" ? (failed ? <Icon size={20} /> : <Spinner size={14} />) : <Icon size={20} />}
      {showName && (ref.kind === "file" || ref.kind === "audio") && <span className="t-caption2 medium clamp2 asset-name">{mediaFileName(ref)}</span>}
    </span>
  );
}

function fileSymbol(ext: string) {
  if (ext === "audio") return AudioLines;
  if (["zip", "rar", "7z", "tar", "gz", "tgz", "bz2", "xz"].includes(ext)) return FileArchive;
  if (["xls", "xlsx", "csv", "numbers"].includes(ext)) return SheetIcon;
  if (["ppt", "pptx", "key"].includes(ext)) return Presentation;
  if (["pdf", "doc", "docx", "pages", "md", "txt", "rtf"].includes(ext)) return FileText;
  if (["html", "htm", "json", "py", "js", "ts", "swift", "sh"].includes(ext)) return Code;
  return File;
}

export function AssetDetail({ item, onOpen }: { item: AssetItem; onOpen: (item: AssetItem) => void }) {
  const app = useApp();
  const ref = assetRef(item);
  const siblings = app.assets.siblings(item).filter((s) => s.source !== item.source);
  const place = item.origin === "cron" ? "定时任务" : item.origin === "telegram" ? "Telegram" : item.origin === "weixin" ? "微信" : "对话";
  const when = new Date(item.timestamp * 1000).toLocaleString("zh-CN", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  const openConversation = () => app.openStored(item.sessionID, app.selectedProfile, item.sessionTitle);
  return (
    <div className="asset-detail">
      <MediaGallery refs={[ref]} />
      <div className="col" style={{ gap: 4 }}>
        <span className="t-headline clamp2">{mediaFileName(ref)}</span>
        <span className="t-footnote t-2">来自{place}{item.sessionTitle ? `「${item.sessionTitle}」` : ""} · {when}</span>
      </div>
      {item.sessionID && (
        <>
          <button className="big-button prominent" onClick={() => { openConversation(); app.activeChat.setDraft(`基于这个文件继续修改（${item.source}）：\n`); }}>
            <WandSparkles size={17} /> 让它继续改
          </button>
          <button className="big-button" onClick={openConversation}><MessagesSquare size={17} /> 回到当时的对话</button>
        </>
      )}
      {siblings.length > 0 && (
        <div className="col" style={{ gap: 8 }}>
          <span className="t-footnote semibold t-2">同一段对话里的其它版本</span>
          <div className="sibling-strip">
            {siblings.map((s) => <button key={s.source} className="asset-cell sibling" onClick={() => onOpen(s)}><AssetThumb item={s} /></button>)}
          </div>
        </div>
      )}
      <span className="t-caption t-3">右键（手机上长按）预览可以下载或拷贝路径。</span>
    </div>
  );
}
